import SaveIcon from '@mui/icons-material/Save';
import { Box, Divider } from '@mui/material';
import { FormActionsMuiCancelAction } from './FormActionsMuiCancelAction';
import { FormActionsMuiDefaultLayout } from './FormActionsMuiDefaultLayout';
import { FormActionsMuiDiscardAction } from './FormActionsMuiDiscardAction';
import { FormActionsMuiDraftAction } from './FormActionsMuiDraftAction';
import { FormActionsMuiSubmitButton } from './FormActionsMuiSubmitButton';
import {
  FormActionsMuiProps,
  FormActionsMuiRenderContainerOptions,
} from './form-actions-mui-types';
import { useFormActionsMui } from './useFormActionsMui';

export type {
  FormActionsMuiRenderContainer,
  FormActionsMuiRenderContainerOptions,
} from './form-actions-mui-types';

/**
 * Standard MUI form footer: cancel/discard on the left, draft/submit on the right.
 * Supports compact icon actions, custom layout via renderContainer, and enter-to-submit hotkey.
 */
export function FormActionsMui({
  saveLabel,
  disableOnSubmit = false,
  disableSave = false,
  successState = false,
  saveIcon = <SaveIcon />,
  cancel,
  separator = false,
  marginTop = 4,
  variant = 'default',
  onCancel,
  onDiscard,
  onDraft,
  renderContainer,
  actionsWrap = false,
  disableSubmitIfInvalid = false,
  submitDisabledTooltip,
  renderSubmit,
}: FormActionsMuiProps) {
  const {
    submitShortcutRef,
    submitHotkey,
    useDiscardDraft,
    allowCancel,
    showTick,
    saveDisabled,
    secondaryDisabled,
    isCompact,
    isSubmitLoading,
    showSubmitHotkey,
    handleCancel,
  } = useFormActionsMui({
    disableOnSubmit,
    disableSave,
    successState,
    cancel,
    onCancel,
    onDiscard,
    onDraft,
    variant,
    disableSubmitIfInvalid,
  });

  const submitProps = {
    saveLabel,
    saveIcon,
    showTick,
    saveDisabled,
    isSubmitLoading,
    showSubmitHotkey,
    submitHotkey,
    submitDisabledTooltip,
  };

  const leftActions = (
    <>
      {useDiscardDraft && onDiscard && (
        <FormActionsMuiDiscardAction
          isCompact={isCompact}
          disabled={secondaryDisabled}
          onDiscard={onDiscard}
        />
      )}
      {allowCancel && (
        <FormActionsMuiCancelAction disabled={secondaryDisabled} onCancel={handleCancel} />
      )}
    </>
  );

  const rightActions = (
    <>
      {useDiscardDraft && onDraft && (
        <FormActionsMuiDraftAction
          isCompact={isCompact}
          disabled={secondaryDisabled}
          onDraft={onDraft}
        />
      )}
      {renderSubmit ? renderSubmit(submitProps) : <FormActionsMuiSubmitButton {...submitProps} />}
    </>
  );

  const renderContainerOptions: FormActionsMuiRenderContainerOptions = { actionsWrap };

  const content = renderContainer ? (
    renderContainer(leftActions, rightActions, renderContainerOptions)
  ) : (
    <FormActionsMuiDefaultLayout
      leftActions={leftActions}
      rightActions={rightActions}
      actionsWrap={actionsWrap}
    />
  );

  return (
    <Box ref={submitShortcutRef} mt={marginTop}>
      {separator && <Divider sx={{ my: 4 }} />}
      {content}
    </Box>
  );
}
